import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useRenameWorkspace } from '../../api/settings-queries';
import { useWorkspaces } from '../../api/queries';
import { useSession } from '../../app/session-context';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { InputField } from '../../ui/Input';
import { Stack } from '../../ui/Stack';
import { StatusState } from '../../ui/StatusState';

/** The workspace's own name (accounts mode). Admin mode has one server-named workspace. */
export function GeneralSettings(): ReactElement {
  const { snapshot } = useSession();
  if (snapshot.mode !== 'accounts') {
    return (
      <StatusState kind="empty" title="This server runs one workspace; its name is set by the server." />
    );
  }
  return <NameForm workspaceId={snapshot.workspaceId} />;
}

function NameForm({ workspaceId }: { workspaceId: string | null }): ReactElement {
  const workspaces = useWorkspaces();
  const rename = useRenameWorkspace();
  const [name, setName] = useState('');
  const dirty = useRef(false);
  const current = workspaces.data?.find((w) => w.id === workspaceId);
  const currentName = current?.name;

  // Prefill from the list, but never over what the person has already typed.
  useEffect(() => {
    if (currentName !== undefined && !dirty.current) setName(currentName);
  }, [currentName]);

  const trimmed = name.trim();
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!workspaceId || trimmed.length === 0) return;
    rename.mutate(
      { id: workspaceId, name: trimmed },
      {
        onSuccess: (updated) => {
          dirty.current = false;
          setName(updated.name);
        }
      }
    );
  };

  return (
    <Card title="Name">
      <form onSubmit={save}>
        <Stack gap={4}>
          <InputField
            label="Workspace name"
            value={name}
            isDisabled={!current}
            onChange={(value) => {
              dirty.current = true;
              setName(value);
            }}
          />
          <ErrorBanner error={workspaces.error ?? rename.error} />
          <div>
            <Button type="submit" variant="primary" isDisabled={!current || trimmed.length === 0} busy={rename.isPending}>
              Save name
            </Button>
          </div>
        </Stack>
      </form>
    </Card>
  );
}
